import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "../lib/seoSchemas";

// Manages document.title, meta tags, canonical, OG/Twitter tags, and JSON-LD
// Renders nothing.
export default function Seo({
  title,
  description,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  schemas = [],
  noIndex = false,
}) {
  const { pathname } = useLocation();
  const url = `${SITE_URL}${pathname}`;

  useEffect(() => {
    if (title) document.title = title;

    const ensureMeta = (selector, attrName, attrValue, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) {
      ensureMeta('meta[name="description"]', "name", "description", description);
    }
    ensureMeta('meta[name="robots"]', "name", "robots", noIndex ? "noindex,nofollow" : "index,follow");

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Open Graph
    ensureMeta('meta[property="og:type"]', "property", "og:type", type);
    ensureMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE_NAME);
    ensureMeta('meta[property="og:url"]', "property", "og:url", url);
    if (title) ensureMeta('meta[property="og:title"]', "property", "og:title", title);
    if (description) ensureMeta('meta[property="og:description"]', "property", "og:description", description);
    if (image) {
      ensureMeta('meta[property="og:image"]', "property", "og:image", image);
      ensureMeta('meta[property="og:image:width"]', "property", "og:image:width", "1200");
      ensureMeta('meta[property="og:image:height"]', "property", "og:image:height", "630");
      ensureMeta('meta[property="og:image:alt"]', "property", "og:image:alt", title || SITE_NAME);
    }

    // Twitter
    ensureMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    if (title) ensureMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    if (description) ensureMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    if (image) ensureMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    // JSON-LD: replace any per-page schema scripts with the new set
    const PAGE_LD_ID = "page-jsonld";
    document.head.querySelectorAll(`script[data-jsonld="${PAGE_LD_ID}"]`).forEach((n) => n.remove());
    schemas.forEach((schema) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.jsonld = PAGE_LD_ID;
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
    });
  }, [title, description, image, type, url, noIndex, schemas]);

  return null;
}
